import {
  cloneDeep,
  each,
  filter,
  find,
  includes,
  isArray,
  isObject,
  keys,
  map,
  set,
  sortBy,
  startCase,
  sumBy,
} from 'lodash-es';

import { dashboardBffClient } from '@/api-client';
import type { ApiResponse, ErrorDetail, K10Action, PolicyP, RunAction } from '@/api-client/api';
import type {
  ActionArtifact,
  GroupedArtifacts,
  SubAction,
} from '@/components/action-details/types';
import constants from '@/constants';
// @ts-ignore
import * as log from '@/logger';
import type { PolicyWithTableSummary } from '@/pages/policies/new/model';
import { fetchPolicies, silentErrorHandler } from '@/services';
import { useAppInfoStore } from '@/stores/appInfo';
import { useMainStore } from '@/stores/main';
import { usePolicyStore } from '@/stores/policy/policy';

/**
 * Group artifacts by type and calculate other metadata numbers
 * @param allArtifacts - array of artifacts
 * @return map of artifacts sorted by type, along with other metadata
 */
export function groupArtifacts(allArtifacts: ActionArtifact[]): GroupedArtifacts {
  const volumes = filter(allArtifacts, (artifact) => !!artifact.meta?.volume);
  const snapshots = filter(allArtifacts, (artifact) => !!artifact.meta?.snapshot);

  return {
    snapshots,
    volumes,
    kanisters: filter(allArtifacts, (artifact) => !!artifact.meta?.kanister),
    specs: filter(allArtifacts, (artifact) => !!artifact.meta?.spec),
    dataservices: filter(allArtifacts, (artifact) => !!artifact.meta?.dataservice),
    totalVolumeSize: volumes.length
      ? sumBy(volumes, (v: ActionArtifact) => v?.meta?.volume?.size || 0)
      : undefined,
    totalSnapshotSize: snapshots.length
      ? sumBy(snapshots, (s: ActionArtifact) => s?.meta?.snapshot?.volume?.size || 0)
      : undefined,
  };
}

/**
 * Translate name (kind) of action to a user-viewable name
 * @param actionKind
 * @return
 */
export function translateActionKind(actionKind: string): string {
  const kind = actionKind
    .replace(constants.actions.IMPORT, 'Import')
    .replace(constants.actions.EXPORT, 'Export')
    .replace(constants.actions.RESTORE, 'Restore')
    .replace(constants.actions.BACKUP, 'Backup')
    .replace(constants.actions.RETIRE, 'Retire')
    .replace(constants.actions.BACKUP_CLUSTER, 'Backup')
    .replace(constants.actions.RESTORE_CLUSTER, 'Restore')
    .replace(constants.actions.RUN, 'Policy Run')
    .replace(constants.actions.REPORT, 'Report')
    .replace(constants.actions.UPGRADE, 'Upgrade')
    .replace(constants.actions.MIGRATE_FCD, 'Migrate FCD')
    .replace(constants.actions.VALIDATE, 'Validate')
    .trim();
  return startCase(kind);
}

/**
 * Action resources have a sub-object according to its type.
 * For example, an action with type 'backupaction' will have a sub-obj called
 * 'backupaction'.  This returns the action object.
 * @param actionResource
 * @returns action sub-object
 */
export function getActionObjFromActionResource(actionResource: K10Action) {
  return (
    actionResource.backupAction ||
    actionResource.restoreAction ||
    actionResource.exportAction ||
    actionResource.importAction ||
    actionResource.retireAction ||
    actionResource.backupClusterAction ||
    actionResource.restoreClusterAction ||
    actionResource.runAction ||
    actionResource.reportAction ||
    actionResource.upgradeAction ||
    actionResource.batchRestoreAction ||
    actionResource.validateAction
  );
}

export const actionId = (name = '', kind = '', uid = '', namespace = '') =>
  `${namespace}:${name}:${uid}:${kind}`;

/**
 * Create a string identifier from the given actionResource
 * @param actionResource K10Action
 * @return identifier
 */
export function idOfAction(actionResource: K10Action) {
  const { name, uid, namespace } = getActionObjFromActionResource(actionResource)?.metadata ?? {};
  const kind = actionResource?.kind || '';

  return actionId(name, kind, uid, namespace);
}

/**
 * Convert action kind to camelcase.
 * @param kind {String} - ex: 'backupaction'
 * @return - ex: 'BackupAction'
 */
// TODO: replace with a map instead of THIS.
export function actionCamelCase(kind: string) {
  if (kind === 'batchrestoreaction') {
    return 'BatchRestoreAction';
  }

  let k = kind;
  if (kind.includes('action')) {
    k = kind.replace('action', 'Action');
    k = k.charAt(0).toUpperCase() + k.slice(1);
    return k;
  } else if (kind.includes('Action')) {
    return k;
  }
  log.warn(`actionCamelCase(): kind ${kind} does not contain "action"`);
  return kind;
}

export const getRunActionDetails = (name: string, namespace: string, ignoreCache = false) =>
  getActionDetailsByRequestGetter(
    () => dashboardBffClient.runactions.getRunActionDetails({ name, namespace }),
    `${name}:${constants.actions.RUN}`,
    ignoreCache,
  );

export const getActionDetailsByResource = (action: K10Action, ignoreCache = false) => {
  const kind = action.kind;
  const { name, namespace, uid } = getActionObjFromActionResource(action)?.metadata ?? {};

  if (kind === constants.actions.RUN) {
    const runActionNamespace = action.runAction?.spec?.subject?.namespace;
    // Assuming name and namespace are available if kind is runAction
    if (name && runActionNamespace) {
      return getRunActionDetails(name, runActionNamespace, ignoreCache);
    }
  }

  return getActionDetailsByQuery(
    {
      name: name || '',
      kind: kind || '',
      namespace: namespace || '',
      uid: uid || '',
    },
    ignoreCache,
  );
};

export const getActionDetailsByQuery = (
  { name, kind, namespace, uid }: { name: string; kind: string; namespace?: string; uid?: string },
  ignoreCache = false,
) => {
  return getActionDetailsByRequestGetter(
    // @ts-ignore
    () => dashboardBffClient.actions.getActionDetails({ name, kind, namespace }),
    actionId(name, kind, uid, namespace),
    ignoreCache,
  );
};

/**
 * Get action details for given namespace/name
 * Will attempt to return cached results first, then go to server.
 * Actions details contains the phases for the action.
 *
 * @param request {function} the url
 * @param cacheKey {string|symbol?}
 * @param ignoreCache {boolean?}
 */
// TODO refactor the whole logic along with action details section
export async function getActionDetailsByRequestGetter(
  request: () =>
    Promise<ApiResponse<K10Action, ErrorDetail>> | Promise<ApiResponse<RunAction, ErrorDetail>>,
  cacheKey: string,
  ignoreCache = false,
): Promise<K10Action | RunAction | typeof constants.ACCESS_DENIED | undefined> {
  const store = useMainStore();
  const fromCache = store.actionDetailsCache[cacheKey];
  if (fromCache && !ignoreCache) return fromCache;

  log.debug('getActionDetails fetching', cacheKey);

  return request()
    .then(({ data }: ApiResponse<K10Action> | ApiResponse<RunAction>) => {
      if (!data) throw new Error();

      store.actionDetailsCache[cacheKey] = data;

      return data;
    })
    .catch((err: any) => {
      store.actionDetailsCache[cacheKey] = null;

      const status = err?.response?.status;

      if ([401, 403].includes(status)) {
        return constants.ACCESS_DENIED;
      }

      silentErrorHandler(err);
    });
}

/**
 * Get policies from k8s labels
 * We represent policies in K8s objects as labels with a specific policy key.
 * e.g.
 * {
 *   "key": "k10.kasten.io/policyName",
 *   "value": "protect-default-namespace"
 * },
 * This returns a list of policy names read from the labels.
 * @param {Array | Object} labels
 * @param {Object} opts
 *                 opts.includeNamespace {Boolean}
 * @return {Array} policy strings
 */
export function policiesFromLabels(
  labels: any[] | Record<string, string>,
  opts = { includeNamespace: true },
) {
  const KEY = 'k10.kasten.io/policyName';
  const KEY_NS = 'k10.kasten.io/policyNamespace';
  if (isArray(labels)) {
    const polLabels = filter(labels, (l) => l.key === KEY);
    return map(polLabels, (l) => l.value);
  } else if (isObject(labels)) {
    const policyName = (labels as Record<string, string>)[KEY];
    if (!policyName) return [];
    const policyNs = (labels as Record<string, string>)[KEY_NS];
    if (policyNs && opts.includeNamespace) return [`${policyNs}/${policyName}`];
    return [policyName];
  }
  log.warn('policiesFromLabels(): labels is neither an array nor an object!', labels);
  return [];
}

/**
 * policyFromLabels
 * Return single policy name found in labels map.
 * @param labels {Object}
 * @param opts - opts.includeNamespace - set to true if policy name should include the namespace.
 * @return {string|null|*}
 */
export function policyFromLabels(
  labels?: Record<string, string> | null,
  opts = { includeNamespace: true },
) {
  const KEY = 'k10.kasten.io/policyName';
  const KEY_NS = 'k10.kasten.io/policyNamespace';
  if (!isObject(labels)) {
    log.error('Labels passed to policyFromLabels() must be an object (map)', labels);
    return null;
  }
  const policyName = labels![KEY];
  if (!policyName) return null;
  const policyNs = labels![KEY_NS];
  if (policyNs && opts.includeNamespace) return `${policyNs}/${policyName}`;
  return policyName;
}

export function exportProfileFromLabels(labels?: Record<string, string> | null) {
  if (!labels) return null;
  const KEY = 'k10.kasten.io/exportProfile';
  return labels[KEY];
}

export function blockModeProfileFromLabels(labels?: Record<string, string> | null) {
  if (!labels) return null;
  const KEY = 'k10.kasten.io/blockModeExportProfile';
  return labels[KEY];
}

export function importProfileFromLabels(labels?: Record<string, string> | null) {
  if (!labels) return null;
  const KEY = 'k10.kasten.io/importProfile';
  return labels[KEY];
}

export async function isK10DrEnabled() {
  const mainStore = useMainStore();
  const appInfoStore = useAppInfoStore();
  const policyStore = usePolicyStore();

  if (mainStore.policies === null) {
    if (window.location.hash.includes('policies') && !window.location.hash.includes('presets')) {
      await policyStore.fetchPoliciesByNamespace(appInfoStore.systemNamespace);
      mainStore.policies = policyStore.policies?.map((p: PolicyWithTableSummary) => p.policy) || [];
    } else {
      await fetchPolicies();
    }
  }

  const drPolicy = mainStore.policies?.find(
    (p: PolicyP) =>
      p.metadata?.name === constants.K10_DR_POLICY_NAME &&
      p.metadata?.namespace === appInfoStore.systemNamespace,
  );
  return {
    enabled: !!drPolicy,
    policy: drPolicy,
  };
}

/**
 * Creates and returns a short, 8-char, random string of letters
 */
export function randomString() {
  return Math.random().toString(36).substring(2, 10);
}

/**
 * Construct a selfLink from the given action object.
 * These are used for creating kubectl commands.
 *
 * Examples:
 * /apis/actions.kio.kasten.io/v1alpha1/namespaces/minio/backupactions/scheduled-wmqjd
 * /apis/actions.kio.kasten.io/v1alpha1/retireactions/retire-pjcdt8tr6f
 */
export function selfLink(action: SubAction) {
  if (action?.metadata?.selfLink) {
    return action.metadata.selfLink;
  }
  let link = `/apis/${action.apiVersion}`;
  if (action.metadata?.namespace) link += `/namespaces/${action.metadata.namespace}`;
  link += `/${action.kind?.toLowerCase()}s`;
  link += `/${action.metadata?.name}`;
  return link;
}

/**
 * DEPRECATED. We can probably remove this logic now that the format of actions have been fixed.
 * Fix the structure of action objects.
 * The action objects fetched from /actions has a top struction of { backupAction: {...}}
 * (https://longevity.gke2.kasten.io/k10/dashboardbff-svc/v0/actions?offset=0&limit=10)
 * @param actionResource
 * @return {*}
 */
export function fixAction(actionResource: any) {
  const action = cloneDeep(actionResource);
  // Fix the action - https://kasten.atlassian.net/browse/K10-4759
  const actionName = find(keys(action), (key) => includes(key.toLowerCase(), 'action'));
  if (actionName) {
    each(sortBy(keys(action[actionName])), (key) => {
      action[key] = action[actionName][key];
    });
    delete action[actionName];
  }
  // Kind might be lowercase.  Should get fixed on backend, but just in case.
  if (action.kind) {
    action.kind = actionCamelCase(action.kind);
  }

  return action;
}

export function maskPolicy(policy: any) {
  const p = cloneDeep(policy);
  const path = 'spec.actions[0].importParameters.receiveString';
  if ((p as any)?.path) {
    set(p, path, '<REDACTED>');
  }
  return p;
}

export function sleep(ms: number) {
  if (import.meta.env.MODE === 'test') return;
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
